import { computed } from "vue";

import { formatUsersForFilterDropdown } from "@/helpers/common";
import { useAuth } from "@/hooks/auth";
import {
  useAnalystsQuery,
  useClientAnalystsQuery,
  useClientFundingAdvisorsQuery,
  useClientUnderwritersQuery,
  useFundingAdvisorsQuery,
  useUnderwritersQuery,
} from "@/hooks/queries/useUsers";

interface UseUsersForFiltersOptions {
  ignoreAssignedDeals?:
    | boolean
    | {
        analysts?: boolean;
        fundingAdvisors?: boolean;
        underwriters?: boolean;
        clientAnalysts?: boolean;
        clientFundingAdvisors?: boolean;
        clientUnderwriters?: boolean;
      };
  enabled?: {
    analysts?: boolean;
    fundingAdvisors?: boolean;
    underwriters?: boolean;
    clientAnalysts?: boolean;
    clientFundingAdvisors?: boolean;
    clientUnderwriters?: boolean;
  };
}

export const useUsersForFilters = (options: UseUsersForFiltersOptions = {}) => {
  const { ignoreAssignedDeals = false, enabled = {} } = options;

  // Handle ignoreAssignedDeals as either boolean or object
  const getIgnoreAssignedDeals = (userType: string) => {
    if (typeof ignoreAssignedDeals === "boolean") {
      return ignoreAssignedDeals;
    }
    return (
      ignoreAssignedDeals[userType as keyof typeof ignoreAssignedDeals] ?? false
    );
  };

  const {
    analysts: enabledAnalysts = true,
    fundingAdvisors: enabledFundingAdvisors = true,
    underwriters: enabledUnderwriters = true,
    clientAnalysts: enabledClientAnalysts = true,
    clientFundingAdvisors: enabledClientFundingAdvisors = true,
    clientUnderwriters: enabledClientUnderwriters = true,
  } = enabled;
  const { currentUser, isLendflowUser } = useAuth();

  const analystsQuery = useAnalystsQuery({
    ignoreAssignedDeals: getIgnoreAssignedDeals("analysts"),
    enabled: enabledAnalysts && isLendflowUser,
  });
  const fundingAdvisorsQuery = useFundingAdvisorsQuery({
    ignoreAssignedDeals: getIgnoreAssignedDeals("fundingAdvisors"),
    enabled: enabledFundingAdvisors && isLendflowUser,
  });
  const underwritersQuery = useUnderwritersQuery({
    ignoreAssignedDeals: getIgnoreAssignedDeals("underwriters"),
    enabled: enabledUnderwriters && isLendflowUser,
  });

  const clientId = computed(() => currentUser?.client_id);
  const clientAnalystsQuery = useClientAnalystsQuery({
    ignoreAssignedDeals: getIgnoreAssignedDeals("clientAnalysts"),
    clientId: clientId.value,
    enabled: enabledClientAnalysts,
  });
  const clientFundingAdvisorsQuery = useClientFundingAdvisorsQuery({
    ignoreAssignedDeals: getIgnoreAssignedDeals("clientFundingAdvisors"),
    clientId: clientId.value,
    enabled: enabledClientFundingAdvisors,
  });
  const clientUnderwritersQuery = useClientUnderwritersQuery({
    ignoreAssignedDeals: getIgnoreAssignedDeals("clientUnderwriters"),
    clientId: clientId.value,
    enabled: enabledClientUnderwriters,
  });

  const analysts = computed(() =>
    formatUsersForFilterDropdown(analystsQuery.data.value || [])
  );

  const fundingAdvisors = computed(() =>
    formatUsersForFilterDropdown(fundingAdvisorsQuery.data.value || [])
  );

  const underwriters = computed(() =>
    formatUsersForFilterDropdown(underwritersQuery.data.value || [])
  );

  const clientAnalysts = computed(() =>
    formatUsersForFilterDropdown(clientAnalystsQuery.data.value || [])
  );

  const clientFundingAdvisors = computed(() =>
    formatUsersForFilterDropdown(clientFundingAdvisorsQuery.data.value || [])
  );

  const clientUnderwriters = computed(() =>
    formatUsersForFilterDropdown(clientUnderwritersQuery.data.value || [])
  );

  const isLoadingAny = computed(
    () =>
      analystsQuery.isLoading.value ||
      fundingAdvisorsQuery.isLoading.value ||
      underwritersQuery.isLoading.value ||
      clientAnalystsQuery.isLoading.value ||
      clientFundingAdvisorsQuery.isLoading.value ||
      clientUnderwritersQuery.isLoading.value
  );

  return {
    analysts,
    fundingAdvisors,
    underwriters,
    clientAnalysts,
    clientFundingAdvisors,
    clientUnderwriters,
    isLoadingAny,
    queries: {
      analysts: analystsQuery,
      fundingAdvisors: fundingAdvisorsQuery,
      underwriters: underwritersQuery,
      clientAnalysts: clientAnalystsQuery,
      clientFundingAdvisors: clientFundingAdvisorsQuery,
      clientUnderwriters: clientUnderwritersQuery,
    },
  };
};
